/**
 * Address & HTML Entity Utility
 * Handles decoding of HTML-encoded characters (like &#x2F; for '/') in UI components
 * without altering database or backend logic.
 */

/**
 * Decodes HTML entities from strings (single-layer or multi-layer encoded).
 * Handles numeric hex (&#x2F;), numeric decimal (&#47;), and named entities (&amp;).
 *
 * @param {string} str - String possibly containing HTML entities
 * @returns {string} - Clean decoded string
 */
export const decodeHtml = (str) => {
  if (!str || typeof str !== "string") return str || ""
  if (!str.includes("&")) return str

  let decoded = str
  let prev = ""
  let count = 0

  while (decoded !== prev && count < 5 && decoded.includes("&")) {
    prev = decoded

    // In browser DOM environment, use textarea parsing
    if (typeof document !== "undefined") {
      try {
        const txt = document.createElement("textarea")
        txt.innerHTML = decoded
        decoded = txt.value
      } catch (e) {
        // Fall back to regex below
      }
    }

    // Explicit entity replacement covering common entities and numeric entities
    decoded = decoded
      .replace(/&amp;/gi, "&")
      .replace(/&#x2F;/gi, "/")
      .replace(/&#47;/g, "/")
      .replace(/&sol;/gi, "/")
      .replace(/&#x27;/gi, "'")
      .replace(/&#39;/g, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))

    count++
  }

  return decoded
}

/**
 * Decodes all text fields in an address object.
 *
 * @param {Object} addr - Address object from API or state
 * @returns {Object} - Address object with decoded fields
 */
export const decodeAddress = (addr) => {
  if (!addr || typeof addr !== "object") return addr
  return {
    ...addr,
    name: decodeHtml(addr.name),
    line_1: decodeHtml(addr.line_1),
    line_2: decodeHtml(addr.line_2),
    city: decodeHtml(addr.city),
    district: decodeHtml(addr.district),
    state: decodeHtml(addr.state),
    country: decodeHtml(addr.country),
    phone_number: decodeHtml(addr.phone_number),
  }
}

/**
 * Formats address lines into clean display strings.
 *
 * @param {Object} addr - Address object
 * @returns {{ street: string, cityStatePin: string, country: string }}
 */
export const formatAddressLines = (addr) => {
  if (!addr) return { street: "", cityStatePin: "", country: "" }
  const decoded = decodeAddress(addr)
  const street = [decoded.line_1, decoded.line_2].filter(Boolean).join(", ")
  const locationParts = [decoded.city, decoded.district, decoded.state].filter(Boolean).join(", ")
  const cityStatePin = decoded.pin_code ? `${locationParts} - ${decoded.pin_code}` : locationParts

  return {
    street,
    cityStatePin,
    country: decoded.country || "",
  }
}
